/**
 * Pure utility functions for fuzzy patient duplicate detection.
 * Used by CreatepatientComponent when a Spoke creates a patient
 * and a Hub trust relationship exists.
 */

/**
 * Normalizes a name part for fuzzy comparison:
 * lowercased, all non-alphanumeric characters removed.
 * e.g. "O'Brien" → "obrien", "MARSH^STANLEY" → "marshstanley"
 */
export function normalizeNamePart(name: string): string {
  return (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Returns true if one normalized string contains the other.
 * Handles prefix/suffix matches and first-name abbreviations.
 * e.g. "john" matches "johnny", "doe" matches "doering"
 */
export function isPartialNameMatch(a: string, b: string): boolean {
  if (!a || !b) return false;
  return a.includes(b) || b.includes(a);
}

/**
 * Returns true if two patients are considered a potential duplicate:
 * DOB must match exactly (after trim), and both first and last names
 * must pass the partial-match test after normalization.
 */
export function isPotentialDuplicate(
  candidate: { firstName: string; lastName: string; dob: string },
  existing:   { firstName: string; lastName: string; dob: string }
): boolean {
  if ((candidate.dob || '').trim() !== (existing.dob || '').trim()) return false;
  return (
    isPartialNameMatch(normalizeNamePart(candidate.lastName),  normalizeNamePart(existing.lastName))  &&
    isPartialNameMatch(normalizeNamePart(candidate.firstName), normalizeNamePart(existing.firstName))
  );
}
